/**
 * Mesure d'audience dans le navigateur (03/10/2026), pilotée par
 * `CookieConsent` selon le choix du visiteur :
 *
 * - accord : gtag.js pour Google Analytics 4, avec le mode consentement v2
 *   (tout refusé par défaut, seule `analytics_storage` accordée) ; PostHog
 *   par import dynamique si sa clé est posée (rien de PostHog dans le premier
 *   chargement de la page) ;
 * - refus ou retrait : Google repasse en refusé et se coupe
 *   (`ga-disable-<ID>`, sinon gtag continuerait d'envoyer des pings sans
 *   cookies), PostHog est réinitialisé puis désactivé, et les cookies
 *   `_ga*` et `ph_*` sont effacés ;
 * - pas encore de choix : rien ne se charge, et les restes d'un ancien
 *   accord (périmé après 6 mois) sont effacés.
 *
 * `track()` n'envoie rien sans accord. Un événement émis avant que le choix
 * soit connu attend (20 au plus) : il part si le visiteur accepte, il est
 * jeté s'il refuse.
 */
import type { PostHog } from "posthog-js";
import { cookieDomains, cookieNames, googleConfig, googleConsent, isAnalyticsCookie, isAnalyticsStorageKey, linkEvent } from "@/lib/analytics";
import type { AnalyticsConfig } from "@/lib/analytics";
import { TRACK_EVENT } from "@/library/shared/track";
import type { TrackDetail } from "@/library/shared/track";

type Gtag = (...args: unknown[]) => void;
type Props = Record<string, string>;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
  }
}

const GTAG_SRC = "https://www.googletagmanager.com/gtag/js";
const MAX_QUEUE = 20;

let state: "unknown" | "granted" | "denied" = "unknown";
let queue: Array<[string, Props]> = [];
let googleId: string | null = null;
let posthog: Promise<PostHog | null> | null = null;

/** Envoie un événement à Google Analytics et à PostHog, seulement après accord. */
export function track(event: string, props: Props = {}): void {
  if (state === "granted") return send(event, props);
  if (state === "unknown" && queue.length < MAX_QUEUE) queue.push([event, props]);
}

function send(event: string, props: Props): void {
  window.gtag?.("event", event, props);
  void posthog?.then((instance) => instance?.capture(event, props));
}

/**
 * Applique le choix : `true` (accord), `false` (refus ou retrait), `null`
 * (pas de choix valable pour l'instant).
 */
export function applyAnalyticsConsent(granted: boolean | null, config: AnalyticsConfig): void {
  if (granted === true) {
    state = "granted";
    loadGoogle(config.gaId);
    if (config.posthog) loadPostHog(config.posthog);
    const pending = queue;
    queue = [];
    for (const [event, props] of pending) send(event, props);
    return;
  }
  if (granted === false) {
    state = "denied";
    queue = [];
  }
  revokeGoogle(config.gaId);
  revokePostHog();
  clearAnalyticsStorage();
}

function disableGoogle(id: string, disabled: boolean): void {
  (window as unknown as Record<string, unknown>)[`ga-disable-${id}`] = disabled;
}

function loadGoogle(id: string): void {
  disableGoogle(id, false);
  if (googleId) {
    window.gtag?.("consent", "update", googleConsent(true));
    return;
  }
  googleId = id;
  window.dataLayer = window.dataLayer ?? [];
  // gtag.js ne lit que des objets `arguments` : pas de tableau, pas de paramètre rest.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer?.push(arguments);
  };
  window.gtag("consent", "default", googleConsent(false));
  window.gtag("consent", "update", googleConsent(true));
  window.gtag("js", new Date());
  window.gtag("config", id, googleConfig());
  const script = document.createElement("script");
  script.async = true;
  script.src = `${GTAG_SRC}?id=${encodeURIComponent(id)}`;
  document.head.appendChild(script);
}

function revokeGoogle(id: string): void {
  if (googleId) window.gtag?.("consent", "update", googleConsent(false));
  disableGoogle(id, true);
}

/**
 * PostHog démarre désactivé (ni capture ni stockage) et n'est activé que par
 * `opt_in_capturing()`, qui envoie aussi la page vue en cours. Désactivé par
 * défaut, il le reste même quand on efface son drapeau de refus du stockage :
 * PostHog relit ce drapeau à chaque capture.
 */
function loadPostHog({ key, host }: { key: string; host: string }): void {
  if (!posthog) {
    posthog = import("posthog-js")
      .then(({ default: instance }) => {
        instance.init(key, {
          api_host: host,
          defaults: "2026-08-30",
          person_profiles: "identified_only",
          capture_pageview: "history_change",
          opt_out_capturing_by_default: true,
          opt_out_persistence_by_default: true,
          // Pas d'enregistrement de session, même activé par erreur dans le projet : la politique de confidentialité n'en parle pas.
          disable_session_recording: true,
        });
        return instance;
      })
      .catch(() => null);
  }
  void posthog.then((instance) => {
    if (state === "granted") instance?.opt_in_capturing({ captureEventName: false });
  });
}

function revokePostHog(): void {
  void posthog?.then((instance) => {
    if (!instance) return;
    // reset() remet le consentement à son état par défaut : il passe AVANT l'opt-out.
    instance.reset();
    instance.opt_out_capturing();
    clearAnalyticsStorage();
    // Une sauvegarde différée de PostHog (250 ms) peut suivre le reset : second passage.
    window.setTimeout(clearAnalyticsStorage, 600);
  });
}

/** Efface les cookies `_ga*` et `ph_*` sur tous les domaines possibles, et le stockage de PostHog. */
function clearAnalyticsStorage(): void {
  const names = cookieNames(document.cookie).filter(isAnalyticsCookie);
  for (const name of names) {
    for (const domain of cookieDomains(window.location.hostname)) {
      document.cookie = `${name}=; Max-Age=0; path=/${domain ? `; domain=${domain}` : ""}`;
    }
  }
  for (const storage of [safeStorage("localStorage"), safeStorage("sessionStorage")]) {
    if (!storage) continue;
    for (const key of Object.keys(storage).filter(isAnalyticsStorageKey)) storage.removeItem(key);
  }
}

function safeStorage(name: "localStorage" | "sessionStorage"): Storage | null {
  try {
    return window[name];
  } catch {
    return null;
  }
}

/**
 * Gestes mesurés sur tout le site : clic sur un lien de réservation Cal.com,
 * de téléphone ou d'email ; premier champ rempli d'un formulaire marqué
 * `data-track-form` ; événements émis par les blocs (`yp:track`). Renvoie
 * la fonction qui retire les écouteurs.
 */
export function installTracking(): () => void {
  const started = new Set<string>();
  const onClick = (event: MouseEvent): void => {
    const link = (event.target as Element | null)?.closest?.("a[href]");
    const tracked = link ? linkEvent(link.getAttribute("href") ?? "") : null;
    if (tracked) track(tracked.event, tracked.props);
  };
  const onInput = (event: Event): void => {
    const form = (event.target as Element | null)?.closest?.("[data-track-form]")?.getAttribute("data-track-form");
    if (!form || started.has(form)) return;
    started.add(form);
    track("form_start", { form });
  };
  const onTrack = (event: Event): void => {
    const detail = (event as CustomEvent<TrackDetail>).detail;
    if (!detail?.event) return;
    if (detail.event === "form_start" && detail.props?.form) {
      if (started.has(detail.props.form)) return;
      started.add(detail.props.form);
    }
    track(detail.event, detail.props);
  };
  document.addEventListener("click", onClick, true);
  document.addEventListener("input", onInput, true);
  window.addEventListener(TRACK_EVENT, onTrack);
  return () => {
    document.removeEventListener("click", onClick, true);
    document.removeEventListener("input", onInput, true);
    window.removeEventListener(TRACK_EVENT, onTrack);
  };
}
